/**
 * Client for the cached public-catalogue reads (videos, topics, categories,
 * profiles, topic-of-day, video stats).
 *
 * Every caller treats this as an optimisation: `PublicReadUnavailable` means
 * "the cached path is not usable in this environment", and the hook then runs its
 * existing direct query. That is deliberate —
 *
 * - the SPA and the API deploy separately, so a client that shipped before (or
 *   without) the API must still render lists;
 * - a transport hiccup should cost latency, not a blank page.
 *
 * So this module never decides *what* the app shows — only where the rows come
 * from. Anything that is not a transport/deployment failure is rethrown
 * untouched, so a real bug in a hook is not swallowed.
 *
 * **No token is sent.** These resources are identity-independent by design: the
 * API runs each read as `anon`, so the reply is the same for every visitor and a
 * shared cache entry cannot leak one learner's data to another. Sending the
 * learner's token would make the answer identity-dependent and therefore unsafe
 * to cache globally.
 */

/** A cached read should be faster than the query it replaces; don't wait long. */
const TIMEOUT_MS = 3_000;

/** Read at call time, not module scope: the tests need to observe the current value. */
function apiBase(): string {
  return ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(/\/+$/, "");
}

export type PublicReadResource =
  | "videos"
  | "video"
  | "topics"
  | "categories"
  | "profiles"
  | "topic-of-day"
  | "stored-transcript"
  | "video-stats";

/** The cached path is not reachable (not deployed, offline, timed out). */
export class PublicReadUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PublicReadUnavailable";
  }
}

function unavailable(message: string): PublicReadUnavailable {
  return new PublicReadUnavailable(message);
}

/**
 * Resource + params → the API route that serves it.
 *
 * The endpoint used to be one POST with a `resource` discriminator; each
 * resource is a route now, so the mapping lives here rather than in every hook.
 * `encodeURIComponent` on the path segments matters: a video id is numeric, but
 * a topic id is a uuid and a malformed value must not become a second path
 * segment.
 */
function pathFor(resource: PublicReadResource, params: Record<string, unknown>): string {
  const id = params.id === undefined ? "" : encodeURIComponent(String(params.id));

  switch (resource) {
    case "videos":
    case "topics": {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === "") continue;
        query.set(key, String(value));
      }
      const suffix = query.toString();
      return `/v1/public/${resource}${suffix ? `?${suffix}` : ""}`;
    }
    case "video":
    case "stored-transcript":
      return resource === "video" ? `/v1/public/videos/${id}` : `/v1/public/videos/${id}/transcript`;
    case "profiles": {
      // The batch RPC takes a list; a repeated query param would be ambiguous.
      const ids = Array.isArray(params.ids) ? params.ids.map(String) : [];
      return `/v1/public/profiles?ids=${encodeURIComponent(ids.join(","))}`;
    }
    case "categories":
    case "topic-of-day":
    case "video-stats":
      return `/v1/public/${resource}`;
  }
}

export async function fetchPublicRead<T>(
  resource: PublicReadResource,
  params: Record<string, unknown> = {},
): Promise<T> {
  const base = apiBase();
  if (!base) {
    throw unavailable("VITE_API_BASE_URL is not configured");
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${base}${pathFor(resource, params)}`, {
      method: "GET",
      signal: controller.signal,
    });

    if (!response.ok) {
      // Includes 404 (route not deployed) and 5xx (the read failed upstream).
      throw unavailable(`public-read responded ${response.status}`);
    }

    const body = (await response.json()) as { data?: unknown; count?: number } | null;
    if (!body || typeof body !== "object" || !("data" in body)) {
      throw unavailable("public-read returned an unexpected body");
    }

    return body as T;
  } catch (error) {
    if (error instanceof PublicReadUnavailable) throw error;
    const message =
      error instanceof DOMException && error.name === "AbortError"
        ? "public-read timed out"
        : error instanceof Error
          ? error.message
          : "public-read is unavailable";
    throw unavailable(message);
  } finally {
    clearTimeout(timer);
  }
}
